import { Tab, Tabs, Box, Fab } from "@mui/material";
import { useState, useEffect } from "react";
import { fetchAuthSession } from '@aws-amplify/auth';
import AddIcon from '@mui/icons-material/Add'
import axios from "axios";
import Header from "../../components/general/Header.tsx";
import LoadingMessage from "../../components/LoadingMessage.js";
import {DeleteConfirmationBox} from "../../components/Confirmation";
import TabPanel, { a11yProps } from "../../components/general/TabPanel.js";
import GoalEntry from "./components/GoalEntry.tsx";
import AddGoal from "./components/AddGoal.js";
import ErrorMessage from "../../components/ErrorMessage.js";
import { useData } from "../../DataContext.js";

const Goals = ({ user }) => {

    const userId = user.userId;

    const [state, setState] = useState('loading');
    const [goals, setGoals] = useState([]);
    const [tabValue, setTabValue] = useState(0);
    const [isTokenReady, setIsTokenReady] = useState(false);
    const [isError, setIsError] = useState(false);

    const [confirmModal, setConfirmModal] = useState(false);
    const [idToDelete, setIdToDelete] = useState(0);

    const { triggerAchievements } = useData();

    useEffect(() => {
        const fetchJwtToken = async () => {
          try {
            const session = await fetchAuthSession();
            const token = session.tokens.idToken;
            axios.defaults.headers['Authorization'] = `Bearer ${token}`;
            setIsTokenReady(true); 
          } catch (error) {
            console.error('Error fetching JWT token:', error);
            setIsError(true); 
          }
        };
    
        fetchJwtToken();
      }, []);

    useEffect(() => {
        if (isTokenReady) {
            axios.get(process.env.REACT_APP_API_HOST + `/api/goals/${userId}`)
            .then(response => {
                const goals = response.data;
                // Format the date strings in the Goals array
                const formattedGoals = (goals !== null) ? goals.map(goal => {
                    return {
                        ...goal,
                        // Format the date fields as dd/mm/yyyy
                        start_date: goal.start_date ? new Date(goal.start_date).toISOString() : '',
                        target_date: goal.target_date ? new Date(goal.target_date).toISOString() : '',
                        original_target_date: goal.original_target_date ? new Date(goal.original_target_date).toISOString() : '',
                        next_review_date: goal.next_review_date ? new Date(goal.next_review_date).toISOString() : null,
                        actual_completed_date: goal.actual_completed_date ? new Date(goal.actual_completed_date).toISOString() : '',
                    };
                })
                : [];
                
                setGoals(formattedGoals);
                setState('view');
            })
            .catch(error => {
                console.error('Error fetching goals:', error)
                setIsError(true);
            })
        }
    }, [userId, isTokenReady]); 

    const handleTabChange = (e, newValue) => {
        setTabValue(newValue);
    };

    const handleConfirmRemove = (id) => {
        setIdToDelete(id);
        setConfirmModal(true);
    }

    const handleDelete = (goal_id) => {
        axios.delete(process.env.REACT_APP_API_HOST + `/api/goals/${userId}/${goal_id}`)
        .then(() => {
            setGoals(goals.filter(item => item.id !== goal_id));
            setConfirmModal(false);
            setIdToDelete(0);
        })
        .catch(error => {
            console.error('Error deleting goal', error);
            setIsError(true);
        });
    }

    const emptyGoal = {
        user_id: userId,
        title: null,
        description: null,
        goal_type: "",
        status: "",
        start_date: new Date().toISOString(),
        target_date: new Date().toISOString(),
        next_review_date: new Date().toISOString(),
        actual_completed_date: new Date().toISOString(),
        rag: "",
        reviews: []
    };

    const handleSubmitGoal = (goal) => {
        axios.post(`${process.env.REACT_APP_API_HOST}/api/goals`, goal)
        .then(response => {
            const { last_id, new_achievements } = response.data;
            triggerAchievements(new_achievements);
            const goalWithId = {...goal, id: last_id};
            setGoals([goalWithId, ...goals]);
            setState('view');
        })
        .catch(error => {
            console.error('Error adding goal review', error);
            setIsError(true);
        });
    }


    const cancelEntry = () => {
        setState('view');
        setConfirmModal(false);
        setIdToDelete(0);
    };

    
    if (isError) return <ErrorMessage handleConfirm={() => setIsError(false)} />;

    if (state === 'loading') {
        return <LoadingMessage title={'Goals'} />
    }

    return (
        <>
            {state === 'view' && <>

                <Header title={'Goals'} />

                <Box sx={{ width: '100%', borderRadius: '10px', borderBottom: 1, borderColor: 'divider', overflow:'hidden' }}>
                    <Tabs value={tabValue} onChange={handleTabChange} aria-label="basic tabs example">
                        <Tab label="In Progress" sx={{width: '50%', fontSize: '1.4rem'}} {...a11yProps(0)}  />
                        <Tab label="Completed" sx={{width: '50%', fontSize: '1.4rem'}} {...a11yProps(1)} />
                    </Tabs>
                </Box>
                
                <TabPanel value={tabValue} index={0}>
                    <div className='entries-in-tabs'>
                        {goals?.filter(goal => goal.status !== 'complete')?.map((goal) => (
                            <GoalEntry key={goal.id} goal={goal} remove={handleConfirmRemove} />
                        ))} 
                    </div>
                </TabPanel>
                <TabPanel value={tabValue} index={1}>
                <div className='entries-in-tabs'>
                        {goals?.filter(goal => goal.status === 'complete')?.map((goal) => (
                            <GoalEntry key={goal.id} goal={goal} remove={handleConfirmRemove} />
                        ))} 
                    </div>
                </TabPanel>
            </>}

            {state === 'entering' && 
                <AddGoal goalHolder={emptyGoal} handleSubmit={handleSubmitGoal} handleCancel={cancelEntry} />
            }
            
            {(state === 'view') &&
                <Fab color="secondary" aria-label="add" onClick={() => { setState('entering')}} style={{ position: 'absolute', bottom: '20px', right: '0px' }}>
                    <AddIcon sx={{ fontSize: '3rem' }} />
                </Fab>
            }    

            {confirmModal === true && 
                <DeleteConfirmationBox message={"Are you sure you want to delete this goal?"} handleConfirm={() => handleDelete(idToDelete)} handleCancel={cancelEntry}/>
            }
        </>
    )
};

export default Goals;